import { useState, useRef, useEffect } from "react";

// ── Airtable ──────────────────────────────────────────────────────────────────
const AT_BASE    = "apphNMdlXAUpLKwV4";
const AT_TOKEN   = "patvIqq23oKyjBT5V.a50f22983ae380e647c8f0011b101adbca8e26ad398f99df75666a0eb0047ed6";
const AT_HEADERS = { Authorization: `Bearer ${AT_TOKEN}`, "Content-Type": "application/json" };
const AT_URL     = (table) => `https://api.airtable.com/v0/${AT_BASE}/${encodeURIComponent(table)}`;

async function atFetch(table) {
  const rows = [];
  let offset = null;
  do {
    const filterFormula = encodeURIComponent(`OR({Status}="published",{Status}="")`);
    const url = AT_URL(table)
      + `?filterByFormula=${filterFormula}`
      + `&sort[0][field]=Submitted+At&sort[0][direction]=desc`
      + (offset ? `&offset=${offset}` : "");
    const res = await fetch(url, { headers: AT_HEADERS });
    const data = await res.json();
    rows.push(...(data.records || []));
    offset = data.offset || null;
  } while (offset);
  return rows;
}


// ── Google Fonts ──────────────────────────────────────────────────────────────
const FONTS = (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400;1,600&family=Caveat:wght@400;600;700&family=Lora:ital,wght@0,400;1,400;1,500&display=swap');
  `}</style>
);

// ── Colors ────────────────────────────────────────────────────────────────────
const C = {
  manila:   "#F5F0E8",
  manilaDk: "#EDE7D9",
  rose:     "#efd3d7",
  roseLt:   "#feeafa",
  sage:     "#cbc0d3",
  sageLt:   "#dee2ff",
  blue:     "#dee2ff",
  blueLt:   "#feeafa",
  lavender: "#cbc0d3",
  lavLt:    "#dee2ff",
  ink:      "#2C2438",
  sepia:    "#8e9aaf",
  sepiaLt:  "#cbc0d3",
  rule:     "#cbc0d3",
  ruleDk:   "#8e9aaf",
};

// ── Publication logos ─────────────────────────────────────────────────────────
const LOGOS = {
  "The New York Times": ({ height = 20 }) => (
    <img src="/logos/nyt.svg" alt="The New York Times" style={{ height, maxWidth:160, objectFit:"contain", objectPosition:"left center", display:"block" }}/>
  ),
  "The Forward": ({ height = 20 }) => (
    <img src="/logos/forward.svg" alt="The Forward" style={{ height, maxWidth:120, objectFit:"contain", objectPosition:"left center", display:"block" }}/>
  ),
  "Gay City News": ({ height = 20 }) => (
    <img src="/logos/gay-city-news.png" alt="Gay City News" style={{ height, maxWidth:140, objectFit:"contain", objectPosition:"left center", display:"block" }}/>
  ),
  "NY1": ({ height = 20 }) => (
    <img src="/logos/ny1.svg" alt="NY1" style={{ height, maxWidth:120, objectFit:"contain", objectPosition:"left center", display:"block" }}/>
  ),
  "New York Daily News": ({ height = 20 }) => (
    <img src="/logos/daily-news.png" alt="New York Daily News" style={{ height, maxWidth:140, objectFit:"contain", objectPosition:"left center", display:"block" }}/>
  ),
};

// ── Articles ──────────────────────────────────────────────────────────────────
const ARTICLES = [
  {
    headline: "Ronnie Eldridge, a Fixture in N.Y. Politics, Dies at 95",
    source: "The New York Times",
    date: "March 5, 2026",
    url: "https://www.nytimes.com/2026/03/05/obituaries/ronnie-eldridge-dead.html?unlocked_article_code=1.Q1A.5aBL.4skgdQ0yW8qr&smid=url-share",
    img: "/press/nyt.jpg",
    imgPos: "50% 50%",
  },
  {
    headline: "Ronnie Eldridge Was One of NYC's Toughest Political Minds",
    source: "The Forward",
    date: "March 2026",
    url: "https://forward.com/news/810670/ronnie-eldridge-remembrance-nyc-jimmy-breslin/",
    img: "/press/forward.jpg",
    imgPos: "50% 50%",
  },
  {
    headline: "Ronnie Eldridge, Longtime LGBTQ Ally, NY Civic Leader, and TV Host, Dies at 95",
    source: "Gay City News",
    date: "March 2026",
    url: "https://gaycitynews.com/ronnie-eldridge-lgbtq-ally-ny-leader-tv-dies-95/",
    img: "/press/gaycity.jpg",
    imgPos: "50% 50%",
  },
  {
    headline: "Ronnie Eldridge Remembered as an Icon of New York Politics",
    source: "NY1",
    date: "March 12, 2026",
    url: "https://ny1.com/nyc/all-boroughs/politics/2026/03/12/ronnie-eldridge-remembered-as-an-icon-of-new-york-politics",
    img: "/press/ny1.jpeg",
    imgPos: "50% 50%",
  },
  {
    headline: "Former NYC Council Member Ronnie Eldridge Dies at 95",
    source: "New York Daily News",
    date: "March 5, 2026",
    url: "https://www.nydailynews.com/2026/03/05/former-nyc-council-member-ronnie-eldridge-dies-at-91/",
    img: "/press/dailynews.jpg",
    imgPos: "50% 50%",
  },
];

// ── Decorative SVG motifs ──────────────────────────────────────────────────────
const Bloom = ({ color = "#efd3d7", size = 28, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" fill="none" style={style}>
    <circle cx="14" cy="14" r="4" fill={color} opacity=".7"/>
    {[0,60,120,180,240,300].map(a => (
      <ellipse key={a} cx={14 + 8*Math.cos(a*Math.PI/180)} cy={14 + 8*Math.sin(a*Math.PI/180)}
        rx="4" ry="2.5"
        transform={`rotate(${a} ${14 + 8*Math.cos(a*Math.PI/180)} ${14 + 8*Math.sin(a*Math.PI/180)})`}
        fill={color} opacity=".3"/>
    ))}
  </svg>
);

// ── Memory card ────────────────────────────────────────────────────────────────
const CARD_COLORS = ["#efd3d7", "#dee2ff", "#feeafa", "#cbc0d3", "#dee2ff"];
const CARD_ROTATIONS = [-0.6, 0.4, -0.3, 0.5, -0.4, 0.2, -0.5, 0.6];
const CARD_CHAR_LIMIT = 300;

function MemoryCard({ memory, i }) {
  const isLong = memory.text.length > CARD_CHAR_LIMIT;
  const [expanded, setExpanded] = useState(false);
  const bg = CARD_COLORS[i % CARD_COLORS.length];
  const rot = CARD_ROTATIONS[i % CARD_ROTATIONS.length];

  return (
    <div style={{
      background: bg, borderRadius: "3px", padding: "1.5rem 1.6rem 1.2rem",
      transform: `rotate(${rot}deg)`,
      boxShadow: "2px 3px 14px rgba(58,42,30,.1)",
      transition: "transform .2s, box-shadow .2s",
      border: `1px solid ${C.manilaDk}`,
      display: "flex", flexDirection: "column",
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = "rotate(0deg) scale(1.02)"; e.currentTarget.style.boxShadow = "3px 6px 22px rgba(58,42,30,.16)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = `rotate(${rot}deg)`; e.currentTarget.style.boxShadow = "2px 3px 14px rgba(58,42,30,.1)"; }}
    >
      <div style={{ marginBottom:".9rem" }}>
        <div style={{
          height: expanded ? "auto" : "8rem",
          overflow: "hidden",
          maskImage: (isLong && !expanded) ? "linear-gradient(to bottom, black 50%, transparent 100%)" : "none",
          WebkitMaskImage: (isLong && !expanded) ? "linear-gradient(to bottom, black 50%, transparent 100%)" : "none",
        }}>
          <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".92rem",
            color: C.ink, lineHeight: 1.75, whiteSpace: "pre-wrap" }}>
            {memory.text}
          </p>
        </div>
        {isLong && (
          <button onClick={() => setExpanded(e => !e)}
            style={{ display:"block", marginTop:".45rem", fontFamily:"'Caveat',cursive",
              fontWeight:600, fontSize:".88rem", color: C.sepia, background:"none",
              border:"none", cursor:"pointer", padding:0, textDecoration:"underline",
              textDecorationStyle:"dotted", textUnderlineOffset:"3px" }}>
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginTop:"auto" }}>
        <span style={{ fontFamily:"'Caveat',cursive", fontSize:"1.05rem",
          color: C.ink, fontWeight:600 }}>— {memory.name}</span>
      </div>
    </div>
  );
}

// ── Photo frame ────────────────────────────────────────────────────────────────
const FRAME_ROTS = [-0.8, 0.5, -0.4, 0.9, -0.7, 0.3];
function PhotoFrame({ photo, i, onClick }) {
  const rot = FRAME_ROTS[i % FRAME_ROTS.length];
  return (
    <div style={{
      background: "#fff",
      padding: "8px 8px 32px",
      boxShadow: "2px 4px 18px rgba(58,42,30,.15)",
      transform: `rotate(${rot}deg)`,
      transition: "transform .25s, box-shadow .25s",
      cursor: "zoom-in",
    }}
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.transform = "rotate(0deg) scale(1.04)"; e.currentTarget.style.boxShadow = "4px 8px 28px rgba(58,42,30,.22)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = `rotate(${rot}deg)`; e.currentTarget.style.boxShadow = "2px 4px 18px rgba(58,42,30,.15)"; }}
    >
      <div style={{ width:"100%", aspectRatio:"4/3", overflow:"hidden", background:C.manilaDk }}>
        <img src={photo.src} alt={photo.cap}
          style={{ width:"100%", height:"100%", objectFit:"cover", display:"block",
            filter:"sepia(18%) contrast(1.05)" }}/>
      </div>
      {photo.cap && (
        <p style={{ fontFamily:"'Caveat',cursive", fontSize:".9rem", color:C.sepia,
          textAlign:"center", marginTop:"6px", lineHeight:1.3 }}>{photo.cap}</p>
      )}
    </div>
  );
}

// ── Section heading ────────────────────────────────────────────────────────────
function SectionHead({ children, accent = "#cbc0d3" }) {
  return (
    <div style={{ textAlign:"center", marginBottom:"3rem" }}>
      <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600, fontStyle:"italic",
        fontSize:"clamp(2rem,4vw,2.8rem)", color:C.ink, lineHeight:1.1,
        marginBottom:".5rem" }}>
        {children}
      </h2>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"8px" }}>
        <div style={{ width:"40px", height:"1.5px", background:accent, opacity:.6 }}/>
        <Bloom color={accent} size={16}/>
        <div style={{ width:"40px", height:"1.5px", background:accent, opacity:.6 }}/>
      </div>
    </div>
  );
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ photos, index, onClose, onNav }) {
  const photo = photos[index];
  const total = photos.length;

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    const handler = e => {
      if (e.key === "ArrowLeft") onNav((index - 1 + total) % total);
      if (e.key === "ArrowRight") onNav((index + 1) % total);
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [index, total, onNav, onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const touchStartX = useRef(null);
  const handleTouchStart = e => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = e => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) onNav((index + 1) % total);
      else         onNav((index - 1 + total) % total);
    }
    touchStartX.current = null;
  };

  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  useEffect(() => {
    const update = () => setIsLandscape(window.innerWidth > window.innerHeight);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={onClose}
      style={{
        position:"fixed", inset:0, zIndex:1000,
        background:"rgba(22,16,32,.96)",
        display:"flex",
        flexDirection: isLandscape ? "row" : "column",
        animation:"fadeIn .2s ease",
        userSelect:"none",
        cursor:"pointer",
      }}
    >
      <style>{`@keyframes fadeIn { from { opacity:0 } to { opacity:1 } }`}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          flex:1, position:"relative", display:"flex", flexDirection:"column",
          alignItems:"center", justifyContent:"center",
          padding:"16px",
          overflow:"hidden", minHeight:0, minWidth:0,
          cursor:"default",
        }}
      >
        <div onClick={e => { e.stopPropagation(); onNav((index - 1 + total) % total); }} style={{
          position:"absolute", left:0, top:0, width:"35%", height:"100%",
          zIndex:10, cursor:"pointer",
        }}/>
        <div onClick={e => { e.stopPropagation(); onNav((index + 1) % total); }} style={{
          position:"absolute", right:0, top:0, width:"35%", height:"100%",
          zIndex:10, cursor:"pointer",
        }}/>
        <div style={{
          position:"relative", zIndex:5, maxWidth:"100%", maxHeight:"100%",
          width:"fit-content",
          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
        }}>
          <img
            src={photo.src}
            alt={photo.cap || ""}
            style={{
              maxWidth:"100%",
              maxHeight: isLandscape ? "78vh" : "none",
              objectFit:"contain",
              boxShadow:"0 8px 40px rgba(0,0,0,.7)",
              borderRadius:"4px",
              display:"block",
              pointerEvents:"none",
            }}
          />
          {(photo.cap || photo.submittedBy) && (
            <div style={{ textAlign:"center", marginTop:"10px", position:"relative", zIndex:5 }}>
              {photo.cap && (
                <p style={{ fontFamily:"'Caveat',cursive", fontSize:"1.15rem",
                  color:"#EDE7D9", marginBottom:"2px" }}>{photo.cap}</p>
              )}
              {photo.submittedBy && (
                <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic",
                  fontSize:".8rem", color:"rgba(237,228,204,.45)" }}>
                  shared by {photo.submittedBy}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {!isLandscape && <>
        <div style={{
          position:"absolute", top:"1rem", left:"1.25rem", zIndex:20,
          fontFamily:"'Lora',serif", fontStyle:"italic",
          fontSize:".95rem", color:"rgba(237,228,204,.6)",
          background:"rgba(255,255,255,.08)", borderRadius:"20px",
          padding:"5px 14px", cursor:"default",
        }}
          onClick={e => e.stopPropagation()}
        >
          {index + 1} / {total}
        </div>
        <button onClick={e => { e.stopPropagation(); onClose(); }} style={{
          position:"absolute", top:".75rem", right:"1rem", zIndex:20,
          background:"#c9a8b8", border:"none", borderRadius:"12px",
          color:"#2C2438", cursor:"pointer", touchAction:"manipulation",
          padding:"10px 20px", minHeight:"48px",
          fontFamily:"'Caveat',cursive", fontWeight:700, fontSize:"1.2rem",
        }}>✕ Close</button>
        <div
          onClick={e => e.stopPropagation()}
          style={{ display:"flex", gap:"1rem", padding:"1rem 1.25rem 1.5rem", flexShrink:0 }}
        >
          {[["← Prev", () => onNav((index-1+total)%total)],
            ["Next →", () => onNav((index+1)%total)]].map(([label, fn]) => (
            <button key={label} onClick={e => { e.stopPropagation(); fn(); }} style={{
              flex:1, minHeight:"58px", border:"2px solid rgba(255,255,255,.2)",
              borderRadius:"14px", background:"rgba(255,255,255,.08)",
              color:"#EDE7D9", cursor:"pointer", touchAction:"manipulation",
              fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:"1.05rem",
            }}>{label}</button>
          ))}
        </div>
      </>}

      {isLandscape && (
        <>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position:"fixed", top:"1rem", left:"1.25rem", zIndex:20,
              fontFamily:"'Lora',serif", fontStyle:"italic",
              fontSize:".95rem", color:"rgba(237,228,204,.6)",
              background:"rgba(255,255,255,.08)", borderRadius:"20px",
              padding:"5px 14px", cursor:"default",
            }}
          >
            {index + 1} / {total}
          </div>
          <button onClick={e => { e.stopPropagation(); onClose(); }} style={{
            position:"fixed", top:".75rem", right:"1rem", zIndex:20,
            background:"#c9a8b8", border:"none", borderRadius:"12px",
            color:"#2C2438", cursor:"pointer", touchAction:"manipulation",
            padding:"10px 20px", minHeight:"48px",
            fontFamily:"'Caveat',cursive", fontWeight:700, fontSize:"1.2rem",
          }}>✕ Close</button>
          <button onClick={e => { e.stopPropagation(); onNav((index - 1 + total) % total); }} style={{
            position:"fixed", left:"1rem", top:"50%", transform:"translateY(-50%)",
            zIndex:20, border:"2px solid rgba(255,255,255,.2)", borderRadius:"14px",
            background:"rgba(255,255,255,.08)", color:"#EDE7D9",
            cursor:"pointer", touchAction:"manipulation",
            minHeight:"64px", padding:"8px 16px",
            fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:"1.1rem",
          }}>← Prev</button>
          <button onClick={e => { e.stopPropagation(); onNav((index + 1) % total); }} style={{
            position:"fixed", right:"1rem", top:"50%", transform:"translateY(-50%)",
            zIndex:20, border:"2px solid rgba(255,255,255,.2)", borderRadius:"14px",
            background:"rgba(255,255,255,.08)", color:"#EDE7D9",
            cursor:"pointer", touchAction:"manipulation",
            minHeight:"64px", padding:"8px 16px",
            fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:"1.1rem",
          }}>Next →</button>
        </>
      )}
    </div>
  );
}

// ── Shared Nav ────────────────────────────────────────────────────────────────
function SiteNav({ links }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const NAV_ITEMS = [
    { label:"The Service",  action: links.service },
    { label:"In the News",  action: links.news },
    { label:"Photos",       action: links.photos },
    { label:"Memories",     action: links.memories },
  ];

  const handleNav = (action) => {
    setMenuOpen(false);
    action();
  };

  const handleHome = () => {
    setMenuOpen(false);
    (links.home || (() => window.scrollTo({ top:0, behavior:"smooth" })))();
  };

  return (
    <>
      <style>{`
        .nav-desktop { display:flex !important; }
        .nav-mobile  { display:none !important; }
        @media (max-width:640px) {
          .nav-desktop { display:none !important; }
          .nav-mobile  { display:flex !important; }
        }
      `}</style>

      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{
          position:"fixed", inset:0, zIndex:199,
          background:"rgba(44,36,56,.18)",
        }}/>
      )}

      <nav style={{
        position:"fixed", top:0, left:0, right:0, zIndex:200,
        background:"#F5F0E8",
        borderBottom:"1px solid #cbc0d370",
        transform:"translateZ(0)",
        WebkitTransform:"translateZ(0)",
        WebkitBackfaceVisibility:"hidden",
        backfaceVisibility:"hidden",
      }}>
        {/* ── Desktop nav ── */}
        <div className="nav-desktop" style={{
          maxWidth:"1200px", margin:"0 auto",
          alignItems:"center", justifyContent:"center",
          gap:"clamp(1.5rem, 4vw, 5rem)", padding:".75rem 2rem",
        }}>
          <button onClick={handleHome}
            style={{ background:"none", border:"none", cursor:"pointer",
              color:"#5a5068", padding:".3rem .5rem", display:"flex", alignItems:"center",
              touchAction:"manipulation", flexShrink:0 }}
            title="Home"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5z"/>
              <path d="M9 21V12h6v9"/>
            </svg>
          </button>
          {NAV_ITEMS.map(l => (
            <button key={l.label} onClick={() => handleNav(l.action)}
              style={{ fontFamily:"'Lora',serif", fontStyle:"italic",
                fontSize:"clamp(.85rem, 1.5vw, 1rem)", color:"#5a5068",
                background:"none", border:"none", cursor:"pointer",
                padding:".3rem", fontWeight:600, whiteSpace:"nowrap",
                touchAction:"manipulation", letterSpacing:".02em" }}
              onMouseEnter={e => e.currentTarget.style.color="#2C2438"}
              onMouseLeave={e => e.currentTarget.style.color="#5a5068"}>
              {l.label}
            </button>
          ))}
        </div>

        {/* ── Mobile nav bar ── */}
        <div className="nav-mobile" style={{
          alignItems:"center", justifyContent:"space-between",
          padding:".7rem 1.25rem", position:"relative",
        }}>
          <button onClick={handleHome}
            style={{ background:"none", border:"none", cursor:"pointer",
              color:"#5a5068", padding:".3rem", display:"flex", alignItems:"center",
              touchAction:"manipulation", zIndex:1 }}
            title="Home"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5z"/>
              <path d="M9 21V12h6v9"/>
            </svg>
          </button>

          <button onClick={handleHome} style={{
            position:"absolute", left:0, right:0, margin:"0 auto",
            width:"fit-content", background:"none", border:"none", cursor:"pointer",
            touchAction:"manipulation", zIndex:0,
          }}>
            <span style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
              fontWeight:600, fontSize:"1.05rem", color:"#5a5068", letterSpacing:".01em" }}>
              Ronnie Eldridge
            </span>
          </button>

          <button onClick={() => setMenuOpen(o => !o)}
            style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontWeight:600,
              fontSize:"1rem", color:"#5a5068", background:"none", border:"none",
              cursor:"pointer", padding:".3rem .5rem", touchAction:"manipulation",
              display:"flex", alignItems:"center", gap:".35rem", zIndex:1 }}>
            {menuOpen ? "Close" : "Menu"}
            <span style={{ fontSize:".75rem", opacity:.7 }}>{menuOpen ? "▲" : "▼"}</span>
          </button>
        </div>

        {/* ── Mobile dropdown ── */}
        {menuOpen && (
          <div style={{
            borderTop:"1px solid #cbc0d370",
            background:"#F5F0E8",
            padding:".5rem 0 .75rem",
          }}>
            {NAV_ITEMS.map(l => (
              <button key={l.label} onClick={() => handleNav(l.action)}
                style={{
                  display:"block", width:"100%", textAlign:"left",
                  fontFamily:"'Lora',serif", fontStyle:"italic", fontWeight:600,
                  fontSize:"1.15rem", color:"#2C2438",
                  background:"none", border:"none", cursor:"pointer",
                  padding:"1rem 1.75rem",
                  touchAction:"manipulation",
                  borderBottom:"1px solid #cbc0d340",
                }}>
                {l.label}
              </button>
            ))}
          </div>
        )}
      </nav>
    </>
  );
}

// ── Share Form (reusable) ─────────────────────────────────────────────────────
function ShareForm({ onSubmit }) {
  const [form, setForm] = useState({ name:"", email:"", text:"", _hp:"" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const allFilled = form.name.trim() && form.email.trim() && form.text.trim();

  const handleSubmit = async () => {
    if (!allFilled || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/submit-memory", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, text: form.text, _hp: form._hp }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.error || "Something went wrong. Please try again.");
        setSubmitting(false);
        return;
      }
      setForm({ name:"", email:"", text:"", _hp:"" });
      setSubmitted(true);
      if (onSubmit) onSubmit();
      setTimeout(() => setSubmitted(false), 6000);
    } catch {
      setError("Network error. Please try again.");
    }
    setSubmitting(false);
  };

  const inputStyle = {
    width:"100%", padding:".65rem .9rem", border:"1.5px solid #cbc0d3",
    borderRadius:"5px", background:"#F5F0E8", fontSize:".92rem",
    color:"#2C2438", fontStyle:"italic", outline:"none", fontFamily:"'Lora',serif",
  };

  if (submitted) {
    return (
      <div className="fadein" style={{ background:"#dee2ff", borderRadius:"8px",
        padding:"2.5rem", textAlign:"center", boxShadow:"2px 4px 18px rgba(58,42,30,.08)" }}>
        <Bloom color="#cbc0d3" size={42} style={{ margin:"0 auto .75rem" }}/>
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
          fontSize:"1.4rem", color:"#2C2438", marginBottom:".5rem" }}>
          Thank you for sharing.
        </p>
        <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic",
          fontSize:".9rem", color:"#8e9aaf", lineHeight:"1.8" }}>
          Your submission is now live on the website. We've also sent you an email with a link to edit it at any time.
        </p>
      </div>
    );
  }

  return (
    <div style={{ background:"#fff", borderRadius:"8px", padding:"2.5rem",
      boxShadow:"2px 4px 20px rgba(58,42,30,.09)", border:"1px solid #cbc0d360" }}>
      {error && (
        <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".85rem",
          color:"#c0392b", marginBottom:"1rem" }}>{error}</p>
      )}
      {[
        { label:"Your name",   key:"name",  type:"input",    placeholder:"Emily E." },
        { label:"Your email",  key:"email", type:"email",    placeholder:"emily@example.com" },
        { label:"Your memory", key:"text",  type:"textarea", placeholder:"Share a story, an impression, a moment you carry with you..." },
      ].map(f => (
        <div key={f.key} style={{ marginBottom:"1.25rem" }}>
          <label style={{ display:"block", fontFamily:"'Caveat',cursive", fontWeight:600,
            fontSize:"1rem", color:"#8e9aaf", marginBottom:".35rem" }}>{f.label}</label>
          {f.type === "textarea" ? (
            <textarea value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]:e.target.value }))}
              placeholder={f.placeholder} rows={8}
              style={{ ...inputStyle, resize:"vertical" }}
              onFocus={e => e.target.style.borderColor="#8e9aaf"}
              onBlur={e => e.target.style.borderColor="#cbc0d3"}/>
          ) : (
            <input type={f.type} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]:e.target.value }))}
              placeholder={f.placeholder} style={inputStyle}
              onFocus={e => e.target.style.borderColor="#8e9aaf"}
              onBlur={e => e.target.style.borderColor="#cbc0d3"}/>
          )}
        </div>
      ))}
      {/* honeypot */}
      <input
        value={form._hp || ""}
        onChange={e => setForm(p => ({ ...p, _hp: e.target.value }))}
        tabIndex={-1} aria-hidden="true"
        style={{ position:"absolute", left:"-9999px", width:"1px", height:"1px", opacity:0 }}
      />
      <button onClick={handleSubmit}
        disabled={!allFilled || submitting}
        style={{ fontFamily:"'Caveat',cursive", fontWeight:700, fontSize:"1.05rem",
          padding:".7rem 2rem", borderRadius:"40px",
          background: allFilled ? "#8e9aaf" : "#cbc0d3",
          color:"#2C2438", border:"none",
          cursor: allFilled ? "pointer" : "default",
          boxShadow: allFilled ? "0 2px 12px #8e9aaf55" : "none",
          transition:"all .2s" }}>
        {submitting ? "Saving..." : "Add my memory"}
      </button>
    </div>
  );
}

// ── Memories Page ─────────────────────────────────────────────────────────────
function MemoriesPage({ memories, memoriesLoading, onBack }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ background:"#F5F0E8", minHeight:"100vh", paddingTop:"52px", overflowX:"hidden" }}>
      {FONTS}
      <style>{`* { box-sizing:border-box; margin:0; padding:0; } @keyframes fadeIn { from{opacity:0} to{opacity:1} } .fadein { animation: fadeIn .7s ease both; } .nav-desktop{display:flex;} .nav-mobile{display:none;} @media(max-width:640px){.nav-desktop{display:none;}.nav-mobile{display:flex;}}`}</style>

      <SiteNav links={{
        home: onBack,
        photos: () => { onBack(); setTimeout(() => document.getElementById("photos-section")?.scrollIntoView({ behavior:"smooth" }), 100); },
        memories: () => window.scrollTo({ top:0, behavior:"smooth" }),
        news: () => { onBack(); setTimeout(() => document.getElementById("news-section")?.scrollIntoView({ behavior:"smooth" }), 100); },
        service: () => { onBack(); setTimeout(() => document.getElementById("service-section")?.scrollIntoView({ behavior:"smooth" }), 100); },
      }}/>

      <div style={{ textAlign:"center", padding:"4rem 2rem 2.5rem" }}>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600, fontStyle:"italic",
          fontSize:"clamp(2rem,5vw,3.2rem)", color:"#2C2438", marginBottom:".5rem" }}>
          Memories of Ronnie
        </h1>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"8px", marginBottom:"1rem" }}>
          <div style={{ width:"40px", height:"1px", background:"#cbc0d3" }}/>
          <Bloom color="#efd3d7" size={14}/>
          <div style={{ width:"40px", height:"1px", background:"#cbc0d3" }}/>
        </div>
      </div>

      <div style={{ maxWidth:"1000px", margin:"0 auto", padding:"0 2rem 3rem" }}>
        {memoriesLoading ? (
          <p style={{ textAlign:"center", fontFamily:"'Lora',serif", fontStyle:"italic",
            color:"#8e9aaf", padding:"4rem 0" }}>Loading memories...</p>
        ) : memories.length === 0 ? (
          <p style={{ textAlign:"center", fontFamily:"'Lora',serif", fontStyle:"italic",
            color:"#8e9aaf", padding:"4rem 0" }}>No memories yet. Be the first to share one below.</p>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(100%,280px),1fr))",
            gap:"2rem" }}>
            {memories.map((m, i) => <MemoryCard key={i} memory={m} i={i}/>)}
          </div>
        )}
      </div>

      <section id="share-form" style={{ padding:"5rem 2rem", borderTop:"1px solid #cbc0d360" }}>
        <div style={{ maxWidth:"600px", margin:"0 auto" }}>
          <SectionHead accent="#cbc0d3">Share Your Memory</SectionHead>
          <ShareForm />
        </div>
      </section>

      <footer style={{ background:"#EDE7D9", borderTop:"1px solid #cbc0d360",
        padding:"3.5rem 2rem", textAlign:"center",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:".6rem" }}>
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
          fontSize:".9rem", color:"#8e9aaf", lineHeight:"1.5" }}>
          <span style={{ fontWeight:600, color:"#2C2438" }}>Ronnie Eldridge</span><br/>
          January 30, 1931 – March 4, 2026
        </p>
        <Bloom color="#c9a8b8" size={36}/>
      </footer>
    </div>
  );
}

// ── Gallery Page ──────────────────────────────────────────────────────────────
const GALLERY_PAGE_SIZE = 12;

function GalleryPage({ photos, onBack, onMemories, onOpen }) {
  const [visible, setVisible] = useState(GALLERY_PAGE_SIZE);
  const sentinelRef = useRef();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (visible >= photos.length) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) setVisible(v => Math.min(v + GALLERY_PAGE_SIZE, photos.length));
    }, { rootMargin:"200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible, photos.length]);

  const shown = photos.slice(0, visible);

  return (
    <div style={{ background:"#F5F0E8", minHeight:"100vh", paddingTop:"52px", overflowX:"hidden" }}>
      {FONTS}
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        .gallery-grid { display:grid; gap:12px; grid-template-columns:repeat(2,1fr); }
        @media (min-width:560px) { .gallery-grid { grid-template-columns:repeat(3,1fr); gap:16px; } }
        @media (min-width:900px) { .gallery-grid { grid-template-columns:repeat(4,1fr); gap:20px; } }
        .nav-desktop{display:flex;} .nav-mobile{display:none;}
        @media(max-width:640px){.nav-desktop{display:none;}.nav-mobile{display:flex;}}
      `}</style>

      <SiteNav links={{
        home: onBack,
        photos: () => window.scrollTo({ top:0, behavior:"smooth" }),
        memories: onMemories,
        news: () => { onBack(); setTimeout(() => document.getElementById("news-section")?.scrollIntoView({ behavior:"smooth" }), 100); },
        service: () => { onBack(); setTimeout(() => document.getElementById("service-section")?.scrollIntoView({ behavior:"smooth" }), 100); },
      }}/>

      <div style={{ textAlign:"center", padding:"4rem 2rem 2.5rem" }}>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600, fontStyle:"italic",
          fontSize:"clamp(2rem,5vw,3.2rem)", color:"#2C2438", marginBottom:".5rem" }}>
          Photos &amp; Videos
        </h1>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"8px" }}>
          <div style={{ width:"40px", height:"1px", background:"#cbc0d3" }}/>
          <Bloom color="#efd3d7" size={14}/>
          <div style={{ width:"40px", height:"1px", background:"#cbc0d3" }}/>
        </div>
      </div>

      <div style={{ maxWidth:"1100px", margin:"0 auto", padding:"0 12px 6rem" }}>
        <div className="gallery-grid">
          {shown.map((p, i) => <PhotoFrame key={i} photo={p} i={i} onClick={() => onOpen(i)}/>)}
        </div>
        {visible < photos.length && (
          <div ref={sentinelRef} style={{ height:"60px", display:"flex",
            alignItems:"center", justifyContent:"center", marginTop:"2rem" }}>
            <span style={{ fontFamily:"'Lora',serif", fontStyle:"italic",
              fontSize:".85rem", color:"#8e9aaf" }}>Loading more…</span>
          </div>
        )}
      </div>

      <footer style={{ background:"#EDE7D9", borderTop:"1px solid #cbc0d360",
        padding:"3.5rem 2rem", textAlign:"center",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:".6rem" }}>
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
          fontSize:".9rem", color:"#8e9aaf", lineHeight:"1.5" }}>
          <span style={{ fontWeight:600, color:"#2C2438" }}>Ronnie Eldridge</span><br/>
          January 30, 1931 – March 4, 2026
        </p>
        <Bloom color="#c9a8b8" size={36}/>
      </footer>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {

  const [memories, setMemories] = useState([]);
  const [memoriesLoading, setMemoriesLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [allPhotos, setAllPhotos] = useState([]);
  const [view, setView] = useState("home");

  const memoriesRef = useRef();
  const photosRef   = useRef();
  const serviceRef  = useRef();
  const newsRef     = useRef();
  const shareRef    = useRef();

  useEffect(() => {
    fetch("https://ronnie-gallery.nate-299.workers.dev/")
      .then(r => r.json())
      .then(data => setAllPhotos(data.photos.map(p => ({ src: p.url, cap: "" }))))
      .catch(console.error);
  }, []);

  useEffect(() => {
    atFetch("Memories")
      .then(records => {
        setMemories(records.map(r => ({
          name: r.fields["Name"] || "",
          text: r.fields["Message"] || "",
        })).filter(m => m.text.trim()));
      })
      .catch(console.error)
      .finally(() => setMemoriesLoading(false));
  }, []);

  const scrollTo = r => {
    if (!r.current) return;
    const top = r.current.getBoundingClientRect().top + window.scrollY - 60;
    window.scrollTo({ top, behavior:"smooth" });
  };

  if (view === "memories") {
    return <MemoriesPage
      memories={memories}
      memoriesLoading={memoriesLoading}
      onBack={() => { setView("home"); window.scrollTo(0,0); }}
    />;
  }

  if (view === "gallery") {
    return <>
      <GalleryPage
        photos={allPhotos}
        onBack={() => { setView("home"); window.scrollTo(0,0); }}
        onMemories={() => { setView("memories"); window.scrollTo(0,0); }}
        onOpen={i => setLightbox(i)}
      />
      {lightbox !== null && <Lightbox photos={allPhotos} index={lightbox} onClose={() => setLightbox(null)} onNav={i => setLightbox(i)}/>}
    </>;
  }

  return (
    <div style={{ background:C.manila, minHeight:"100vh", fontFamily:"'Lora',serif", paddingTop:"52px", overflowX:"hidden" }}>
      {FONTS}
      <style>{`
        * { box-sizing:border-box; margin:0; padding:0; }
        body { background:${C.manila}; }
        ::selection { background:${C.rose}; }
        textarea, input { font-family:'Lora',serif; font-size:16px; }
        button { touch-action:manipulation; }
        html, body, #root { overflow-x:hidden; max-width:100vw; -webkit-overflow-scrolling:touch; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
        @keyframes bearWave { 0%,100% { transform:rotate(-5deg); } 50% { transform:rotate(5deg); } }
        @keyframes bearFloat { 0%,100% { transform:translateY(0) rotate(-2deg); } 50% { transform:translateY(-8px) rotate(2deg); } }
        .bear-wave { animation: bearWave 1.8s ease-in-out infinite; transform-origin: bottom center; }
        .bear-float { animation: bearFloat 4s ease-in-out infinite; }
        .fadein { animation: fadeUp .7s ease both; }
        .article-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(58,42,30,.13) !important; }
        .article-card { transition: transform .15s, box-shadow .15s; }
        .nav-desktop { display:flex; }
        .nav-mobile  { display:none; }
        @media (max-width:640px) {
          .nav-desktop { display:none; }
          .nav-mobile  { display:flex; }
        }
      `}</style>

      <SiteNav links={{
        home: () => window.scrollTo({ top:0, behavior:"smooth" }),
        photos: () => scrollTo(photosRef),
        memories: () => scrollTo(memoriesRef),
        news: () => scrollTo(newsRef),
        service: () => scrollTo(serviceRef),
      }}/>

      {/* ── HERO ── */}
      <header style={{ textAlign:"center", padding:"2.5rem 2rem 2.5rem", position:"relative", overflow:"hidden" }}>
        <div style={{ width:"260px", height:"260px", borderRadius:"50%", overflow:"hidden",
          margin:"0 auto 1.5rem", border:`5px solid #fff`,
          boxShadow:"0 0 0 3px #efd3d7, 4px 8px 24px rgba(58,42,30,.14)" }}>
          <img src="/photos/ronnie-crop.png" alt="Ronnie Eldridge"
            style={{ width:"100%", height:"100%", objectFit:"cover",
              objectPosition:"50% 18%", filter:"sepia(12%) contrast(1.05)" }}/>
        </div>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600, fontStyle:"italic",
          fontSize:"clamp(2.4rem,6vw,4rem)", color:C.ink, lineHeight:1,
          letterSpacing:"-.02em", marginBottom:".6rem" }}>
          Ronnie Eldridge
        </h1>
        <p style={{ fontFamily:"'Caveat',cursive", fontSize:"1.15rem", color:C.sepia, marginBottom:".75rem" }}>
          January 30, 1931 – March 4, 2026
        </p>
        <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:"1rem",
          color:C.sepia, maxWidth:"480px", margin:"0 auto 1.75rem", lineHeight:1.7 }}>
          Politician, activist, mother, and friend. One of the great New Yorkers
          of the twentieth and twenty-first centuries. She is missed by many.
        </p>
        <div style={{ display:"flex", justifyContent:"center", gap:".5rem", flexWrap:"nowrap" }}>
          {[
            { label:"Photos",       action: () => scrollTo(photosRef),   bg:"#efd3d7", border:"#d4a8b0" },
            { label:"Memories",     action: () => scrollTo(memoriesRef), bg:"#dee2ff", border:"#a8aed4" },
            { label:"The Service",  action: () => scrollTo(serviceRef),  bg:"#cbc0d3", border:"#a89db5" },
          ].map(b => (
            <button key={b.label} onClick={b.action}
              style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".85rem", fontWeight:700,
                color:"#2C2438", background:b.bg, border:`1.5px solid ${b.border}`, borderRadius:"8px",
                padding:".5rem .8rem", cursor:"pointer", whiteSpace:"nowrap",
                transition:"filter .15s, transform .15s", touchAction:"manipulation" }}
              onMouseEnter={e => { e.currentTarget.style.filter="brightness(.95)"; e.currentTarget.style.transform="translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.filter="none"; e.currentTarget.style.transform="none"; }}>
              {b.label}
            </button>
          ))}
        </div>
      </header>

      {/* ── THE SERVICE ── */}
      <section id="service-section" ref={serviceRef} style={{ padding:"4rem 2rem", background:"#dee2ff",
        borderTop:"1px solid #cbc0d360", borderBottom:"1px solid #cbc0d360", position:"relative" }}>
        <div style={{ maxWidth:"720px", margin:"0 auto" }}>
          <SectionHead accent="#8e9aaf">The Service</SectionHead>
          <div style={{ background:"#000", borderRadius:"8px",
            boxShadow:"2px 4px 20px rgba(58,42,30,.1)", overflow:"hidden",
            position:"relative", paddingTop:"56.25%" }}>
            <iframe
              title="Memorial Service"
              src="https://www.youtube.com/embed/DWPi2h-1l_4?si=mPH0-wj_j1N7BYMb&rel=0&modestbranding=1"
              frameBorder="0"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%" }}
            />
          </div>
        </div>
      </section>

      {/* ── PROGRAM ── */}
      <section id="program-section" style={{ padding:"4rem 2rem", background:"#feeafa",
        borderTop:"1px solid #cbc0d360", borderBottom:"1px solid #cbc0d360" }}>
        <div style={{ maxWidth:"640px", margin:"0 auto" }}>
          <SectionHead accent="#efd3d7">Program</SectionHead>
          <div style={{ textAlign:"center", marginBottom:"2.5rem" }}>
            <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".95rem", color:C.sepia, lineHeight:1.8 }}>
              March 11, 2026
            </p>
            <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".9rem", color:C.sepia, lineHeight:1.6 }}>
              The New York Society for Ethical Culture<br/>Adler Hall
            </p>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:"1.5rem", textAlign:"center" }}>
            <div>
              <p style={{ fontFamily:"'Caveat',cursive", fontWeight:600, fontSize:".88rem",
                color:C.sepia, textTransform:"uppercase", letterSpacing:".08em", marginBottom:".5rem" }}>Music</p>
              <p style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600, fontStyle:"italic",
                fontSize:"1.1rem", color:C.ink, lineHeight:1.5 }}>
                Michiko Inoue, piano · Margot Pantoga, cello
              </p>
              <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".85rem",
                color:C.sepia, lineHeight:1.6, marginTop:".2rem" }}>
                Senior, LaGuardia High School of Music & Art and Performing Arts
              </p>
            </div>
            <div style={{ height:"1px", background:"#cbc0d3", opacity:.5 }}/>
            <div>
              <p style={{ fontFamily:"'Caveat',cursive", fontWeight:600, fontSize:".88rem",
                color:C.sepia, textTransform:"uppercase", letterSpacing:".08em", marginBottom:".5rem" }}>Video</p>
              <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".9rem", color:C.ink, lineHeight:1.7 }}>
                Highlights from CUNY-TV's <em>Keeping Relevant with Ronnie Eldridge</em> and <em>Eldridge & Co.</em>
              </p>
            </div>
            <div style={{ height:"1px", background:"#cbc0d3", opacity:.5 }}/>
            <div>
              <p style={{ fontFamily:"'Caveat',cursive", fontWeight:600, fontSize:".88rem",
                color:C.sepia, textTransform:"uppercase", letterSpacing:".08em", marginBottom:".5rem" }}>Welcome</p>
              <p style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600, fontStyle:"italic",
                fontSize:"1.1rem", color:C.ink }}>Eldridge family</p>
            </div>
            <div style={{ height:"1px", background:"#cbc0d3", opacity:.5 }}/>
            <div>
              <p style={{ fontFamily:"'Caveat',cursive", fontWeight:600, fontSize:".88rem",
                color:C.sepia, textTransform:"uppercase", letterSpacing:".08em", marginBottom:".8rem" }}>Speakers</p>
              <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
                {[
                  { name:"Lisa Gugenheim", role:"Ronnie's City Council Chief of Staff, 1990–1995" },
                  { name:"Gordon Davis · Richard Aurelio", role:"Secretary to the Mayor's Cabinet · First Deputy Mayor for Mayor John Lindsay" },
                  { name:"Ann Northrop", role:"Friend, Activist, Co-host Gay USA" },
                  { name:"Gabrielle Carlson", role:"Friend, Gabrielle Carlson Studio" },
                  { name:"Kathryn Grunes Moss", role:"Friend" },
                ].map(s => (
                  <div key={s.name}>
                    <p style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600, fontStyle:"italic",
                      fontSize:"1.1rem", color:C.ink, lineHeight:1.4 }}>{s.name}</p>
                    <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".82rem",
                      color:C.sepia, lineHeight:1.6, marginTop:".1rem" }}>{s.role}</p>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ height:"1px", background:"#cbc0d3", opacity:.5 }}/>
            <div>
              <p style={{ fontFamily:"'Caveat',cursive", fontWeight:600, fontSize:".88rem",
                color:C.sepia, textTransform:"uppercase", letterSpacing:".08em", marginBottom:".8rem" }}>Family</p>
              <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
                {[
                  { name:"Dan Eldridge, Emily Eldridge and Lucy Eldridge", role:"Children" },
                  { name:"Glynnis Eldridge, Nathaniel Eldridge and Soph Silberman", role:"Grandchildren" },
                  { name:"Michael Silberman", role:"Son-in-Law" },
                ].map(s => (
                  <div key={s.name}>
                    <p style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600, fontStyle:"italic",
                      fontSize:"1.1rem", color:C.ink, lineHeight:1.4 }}>{s.name}</p>
                    <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".82rem",
                      color:C.sepia, lineHeight:1.6, marginTop:".1rem" }}>{s.role}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ marginTop:"2rem", textAlign:"center" }}>
            <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".82rem", color:C.sepia, lineHeight:1.9 }}>
              We could not have made this memorial happen without the generous and loving help of friends-forever-friends: Mary Barber, Lisa Gugenheim, Jon Houston, Brenda Levin, Jessica Mates and Eve-Lynn Schefer. And all of Ronnie's beloved colleagues at CUNY TV.
            </p>
          </div>
        </div>
      </section>

      {/* ── IN THE NEWS ── */}
      <section id="news-section" ref={newsRef} style={{ padding:"4rem 2rem", background:C.manilaDk,
        borderTop:"1px solid #cbc0d360", borderBottom:"1px solid #cbc0d360" }}>
        <div style={{ maxWidth:"900px", margin:"0 auto" }}>
          <SectionHead accent="#efd3d7">In the News</SectionHead>
          <div style={{
            display:"grid",
            gridTemplateColumns:"repeat(auto-fill, minmax(min(100%, 380px), 1fr))",
            gap:"1.25rem",
            alignItems:"stretch",
          }}>
            {ARTICLES.map((a, i) => (
              <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                style={{
                  display:"flex", flexDirection:"column",
                  textDecoration:"none",
                  background:"#F5F0E8",
                  border:"1.5px solid #e0d4e8",
                  borderRadius:"10px",
                  overflow:"hidden",
                  boxShadow:"0 2px 10px rgba(58,42,30,.07)",
                  transition:"transform .15s, box-shadow .15s",
                  height:"100%",
                  minHeight:"380px",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow="0 8px 24px rgba(58,42,30,.13)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="0 2px 10px rgba(58,42,30,.07)"; }}
              >
                {/* Image */}
                <div style={{ width:"100%", aspectRatio:"16/9", overflow:"hidden",
                  background:C.manilaDk, flexShrink:0 }}>
                  <img
                    src={a.img}
                    alt={a.headline}
                    style={{ width:"100%", height:"100%", objectFit:"cover",
                      objectPosition: a.imgPos || "50% 50%",
                      filter:"sepia(12%) contrast(1.04)" }}
                  />
                </div>
                {/* Text */}
                <div style={{ padding:"1.1rem 1.25rem 1.25rem", display:"flex",
                  flexDirection:"column", flex:1 }}>
                  <p style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600,
                    fontStyle:"italic", fontSize:"1.1rem", color:C.ink,
                    lineHeight:1.4, marginBottom:".75rem", flex:1 }}>
                    {a.headline}
                  </p>
                  <div style={{ display:"flex", alignItems:"center" }}>
                    {(() => { const Logo = LOGOS[a.source]; return Logo
                      ? <Logo height={22}/>
                      : <span style={{ fontFamily:"'Caveat',cursive", fontWeight:700, fontSize:".88rem", color:C.sepia }}>{a.source}</span>;
                    })()}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── PHOTOS ── */}
      <section id="photos-section" ref={photosRef} style={{ padding:"5rem 2rem", position:"relative", overflow:"hidden" }}>
        <div style={{ maxWidth:"960px", margin:"0 auto" }}>
          <SectionHead accent="#cbc0d3">Photos and Videos</SectionHead>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(100%, 220px),1fr))",
            gap:"2rem", padding:"1rem" }}>
            {allPhotos.slice(0, 6).map((p, i) => <PhotoFrame key={i} photo={p} i={i} onClick={() => setLightbox(i)}/>)}
          </div>
          <div style={{ textAlign:"center", marginTop:"2.5rem" }}>
            <button onClick={() => { setView("gallery"); window.scrollTo(0,0); }}
              style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontWeight:600, fontSize:"1.2rem",
                color:"#2C2438", background:"#feeafa", border:"2px solid #cbc0d3",
                borderRadius:"12px", cursor:"pointer", padding:"1rem 2.5rem",
                boxShadow:"0 2px 10px rgba(58,42,30,.07)", transition:"transform .15s, box-shadow .15s" }}
              onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 5px 16px rgba(58,42,30,.13)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 2px 10px rgba(58,42,30,.07)"; }}>
              View all photos &amp; videos
            </button>
          </div>
        </div>
      </section>

      {/* ── MEMORIES ── */}
      {memories.length > 0 && (
        <section id="memories-section" ref={memoriesRef} style={{ padding:"5rem 2rem",
          background:"#EDE7D9", borderTop:"1px solid #cbc0d360", overflow:"hidden" }}>
          <div style={{ maxWidth:"960px", margin:"0 auto" }}>
            <div style={{ textAlign:"center", marginBottom:"3rem" }}>
              <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600, fontStyle:"italic",
                fontSize:"clamp(2rem,4vw,2.8rem)", color:C.ink }}>Memories of Ronnie</h2>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"8px", marginTop:".4rem" }}>
                <div style={{ width:"40px", height:"1.5px", background:C.rose, opacity:.6 }}/>
                <Bloom color={C.rose} size={16}/>
                <div style={{ width:"40px", height:"1.5px", background:C.rose, opacity:.6 }}/>
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(100%,280px),1fr))", gap:"2rem" }}>
              {memories.slice(0, 6).map((m, i) => <MemoryCard key={i} memory={m} i={i}/>)}
            </div>
            <div style={{ textAlign:"center", marginTop:"2.5rem" }}>
              <button onClick={() => { setView("memories"); window.scrollTo(0,0); }}
                style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontWeight:600, fontSize:"1.2rem",
                  color:"#2C2438", background:"#efd3d7", border:"2px solid #cbc0d3",
                  borderRadius:"12px", cursor:"pointer", padding:"1rem 2.5rem",
                  boxShadow:"0 2px 10px rgba(58,42,30,.07)", transition:"transform .15s, box-shadow .15s" }}
                onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 5px 16px rgba(58,42,30,.13)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 2px 10px rgba(58,42,30,.07)"; }}>
                Read all memories
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── SHARE FORM ── */}
      <section id="share-form" ref={shareRef} style={{ padding:"5rem 2rem", borderTop:"1px solid #cbc0d360", position:"relative", overflow:"hidden" }}>
        <div style={{ maxWidth:"600px", margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:"1rem" }}>
            <img src="/photos/polarbear3.svg" alt="polar bear" style={{ width:60, height:60, opacity:.85, display:"block", margin:"0 auto" }} className="bear-wave"/>
          </div>
          <SectionHead accent="#cbc0d3">Share Your Memory</SectionHead>
          <ShareForm />
        </div>
      </section>

      {/* ── DONATIONS ── */}
      <section style={{ padding:"4rem 2rem", background:"#EDE7D9", borderTop:"1px solid #cbc0d360", textAlign:"center" }}>
        <div style={{ maxWidth:"560px", margin:"0 auto" }}>
          <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".95rem",
            color:"#2C2438", lineHeight:"1.9", marginBottom:"2rem" }}>
            In lieu of flowers, please consider a donation to one of Ronnie's favorite organizations:
          </p>
          <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
            {[
              { name:"The New York Civil Liberties Union", url:"https://www.nyclu.org/", short:"nyclu.org" },
              { name:"Adaptive Design Association", url:"https://adaptivedesign.funraise.org/", short:"adaptivedesign.funraise.org" },
              { name:"The Queens Public Library", url:"https://queenslibrary.org/", short:"queenslibrary.org" },
            ].map(org => (
              <a key={org.url} href={org.url} target="_blank" rel="noopener noreferrer"
                style={{ display:"block", background:"#F5F0E8", border:"1.5px solid #cbc0d3",
                  borderRadius:"10px", padding:"1rem 1.5rem", textDecoration:"none",
                  transition:"transform .15s, box-shadow .15s", boxShadow:"0 2px 8px rgba(58,42,30,.06)" }}
                onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 6px 18px rgba(58,42,30,.12)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="0 2px 8px rgba(58,42,30,.06)"; }}>
                <p style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:600, fontStyle:"italic",
                  fontSize:"1.15rem", color:"#2C2438", marginBottom:".2rem" }}>{org.name}</p>
                <p style={{ fontFamily:"'Lora',serif", fontStyle:"italic", fontSize:".8rem", color:"#8e9aaf" }}>{org.short}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {lightbox !== null && <Lightbox photos={allPhotos} index={lightbox} onClose={() => setLightbox(null)} onNav={i => setLightbox(i)}/>}

      <footer style={{ background:"#EDE7D9", borderTop:"1px solid #cbc0d360",
        padding:"3.5rem 2rem", textAlign:"center",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:".6rem" }}>
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
          fontSize:".95rem", color:C.sepia, lineHeight:"1.5" }}>
          <span style={{ fontWeight:600, color:"#2C2438" }}>Ronnie Eldridge</span><br/>
          January 30, 1931 – March 4, 2026
        </p>
        <Bloom color="#c9a8b8" size={36}/>
      </footer>
    </div>
  );
}